import { useState } from 'react';
import { Wheel, CustomField } from '../../../../types';

export interface WheelFormData {
  name: string;
  slug: string;
  status: 'draft' | 'active' | 'paused' | 'archived';
  customFields?: CustomField[];
  spinsPerWhatsapp?: number;
  blockByIp?: boolean;
  themeId?: string | null;
  prizePickupInstructions?: string;
  isShared?: boolean;
  instantDraw?: boolean;
  drawDate?: string | null;
  termsType?: 'external' | 'internal' | null;
  termsContent?: string;
  prerequisiteContent?: string;
  loserMessage?: string;
  scheduledMessage?: string;
  simplifiedForm?: boolean;
}

const initialFormData: WheelFormData = {
  name: '',
  slug: '',
  status: 'draft',
  customFields: [],
  spinsPerWhatsapp: 0,
  blockByIp: false,
  themeId: null,
  prizePickupInstructions: '',
  isShared: true,
  instantDraw: true,
  drawDate: null,
  termsType: null as any,
  termsContent: '',
  prerequisiteContent: '',
  loserMessage: '',
  scheduledMessage: '',
  simplifiedForm: false
};

export const useWheelForm = () => {
  const [formData, setFormData] = useState<WheelFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof WheelFormData, string>>>({});

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof WheelFormData, string>> = {};

    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }

    if (!formData.slug.trim()) {
      errors.slug = 'Slug é obrigatório';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      errors.slug = 'Slug deve conter apenas letras minúsculas, números e hífens';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof WheelFormData, value: string | number | null | CustomField[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug from name
    if (field === 'name' && typeof value === 'string') {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setFormErrors({});
  };

  const setEditingWheel = (wheel: Wheel) => {
    setFormData({
      name: wheel.name,
      slug: wheel.slug,
      status: wheel.status,
      customFields: wheel.custom_fields || [],
      spinsPerWhatsapp: wheel.spins_per_whatsapp || 0,
      blockByIp: wheel.block_by_ip || false,
      themeId: (wheel as any).theme_id || null,
      prizePickupInstructions: wheel.prize_pickup_instructions || '',
      isShared: wheel.is_shared !== undefined ? wheel.is_shared : true,
      instantDraw: wheel.instant_draw !== undefined ? wheel.instant_draw : true,
      drawDate: wheel.draw_date || null,
      termsType: (wheel.terms_type || null) as any,
      termsContent: wheel.terms_content || '',
      prerequisiteContent: wheel.prerequisite_content || '',
      loserMessage: (wheel as any).loser_message || '',
      scheduledMessage: (wheel as any).scheduled_message || '',
      simplifiedForm: (wheel as any).simplified_form || false
    });
    setFormErrors({});
  };

  return {
    formData,
    formErrors,
    validateForm,
    handleInputChange,
    resetForm,
    setEditingWheel
  };
};
